import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Badge,
  Button,
  Spinner,
  Code,
  useToast,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Tag,
  Flex,
} from '@chakra-ui/react';
import { ScrollText, Eye, EyeOff, RefreshCw, FileSliders } from 'lucide-react';
import { listAdminFrameworks, getAdminFramework, type FrameworkSummary } from '../lib/api';

interface RawFramework {
  framework_id: string;
  version: string;
  status: string;
  brand_identity: {
    brand_name?: string;
    author_name?: string;
    location?: string;
    primary_color?: string;
    secondary_color?: string;
    [k: string]: unknown;
  };
  voice?: Record<string, unknown>;
  structure?: Record<string, unknown>;
  bans?: Record<string, unknown>;
  sources?: Record<string, unknown>;
  production?: Record<string, unknown>;
  reference_script?: string;
  [k: string]: unknown;
}

function CopyableJSON({ value }: { value: unknown }) {
  return (
    <Box
      as="pre"
      bg="gray.900"
      color="green.300"
      p={3}
      borderRadius="md"
      fontSize="xs"
      maxH="320px"
      overflow="auto"
      whiteSpace="pre-wrap"
      wordBreak="break-word"
    >
      <Code bg="transparent" color="green.300" fontSize="xs">
        {JSON.stringify(value, null, 2)}
      </Code>
    </Box>
  );
}

function FrameworkDetail({ data }: { data: RawFramework }) {
  return (
    <Accordion allowMultiple defaultIndex={[0]}>
      <AccordionItem borderColor="gray.200">
        <h2>
          <AccordionButton>
            <Flex flex="1" align="center" gap={2}>
              <Text fontWeight="600" fontSize="sm">
                Brand Identity
              </Text>
              {data.brand_identity?.brand_name && (
                <Tag size="sm" colorScheme="brand">
                  {String(data.brand_identity.brand_name)}
                </Tag>
              )}
            </Flex>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={3}>
          <CopyableJSON value={data.brand_identity} />
        </AccordionPanel>
      </AccordionItem>

      {data.voice && (
        <AccordionItem borderColor="gray.200">
          <h2>
            <AccordionButton>
              <Text flex="1" textAlign="left" fontWeight="600" fontSize="sm">
                Voice
              </Text>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={3}>
            <CopyableJSON value={data.voice} />
          </AccordionPanel>
        </AccordionItem>
      )}

      {data.structure && (
        <AccordionItem borderColor="gray.200">
          <h2>
            <AccordionButton>
              <Text flex="1" textAlign="left" fontWeight="600" fontSize="sm">
                Structure
              </Text>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={3}>
            <CopyableJSON value={data.structure} />
          </AccordionPanel>
        </AccordionItem>
      )}

      {data.bans && (
        <AccordionItem borderColor="gray.200">
          <h2>
            <AccordionButton>
              <Text flex="1" textAlign="left" fontWeight="600" fontSize="sm">
                Bans
              </Text>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={3}>
            <CopyableJSON value={data.bans} />
          </AccordionPanel>
        </AccordionItem>
      )}

      {data.sources && (
        <AccordionItem borderColor="gray.200">
          <h2>
            <AccordionButton>
              <Text flex="1" textAlign="left" fontWeight="600" fontSize="sm">
                Sources & Citations
              </Text>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={3}>
            <CopyableJSON value={data.sources} />
          </AccordionPanel>
        </AccordionItem>
      )}

      {data.production && (
        <AccordionItem borderColor="gray.200">
          <h2>
            <AccordionButton>
              <Text flex="1" textAlign="left" fontWeight="600" fontSize="sm">
                Production Formatting
              </Text>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={3}>
            <CopyableJSON value={data.production} />
          </AccordionPanel>
        </AccordionItem>
      )}

      {data.reference_script && (
        <AccordionItem borderColor="gray.200">
          <h2>
            <AccordionButton>
              <HStack flex="1" textAlign="left" spacing={2}>
                <ScrollText size={14} />
                <Text fontWeight="600" fontSize="sm">
                  Reference Script (locked teleprompter copy)
                </Text>
              </HStack>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel pb={3}>
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              borderWidth={1}
              borderColor="gray.200"
              maxH="500px"
              overflowY="auto"
              fontSize="sm"
              color="gray.700"
              lineHeight="1.7"
              whiteSpace="pre-wrap"
            >
              {data.reference_script}
            </Box>
          </AccordionPanel>
        </AccordionItem>
      )}
    </Accordion>
  );
}

export default function FrameworkAdminViewer() {
  const toast = useToast();
  const [summaries, setSummaries] = useState<FrameworkSummary[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, RawFramework | 'loading' | 'error'>>({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await listAdminFrameworks();
      setSummaries(rows);
    } catch {
      toast({
        title: 'Failed to load framework configurations',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function handleToggle(siteId: string) {
    if (expanded === siteId) {
      setExpanded(null);
      return;
    }
    setExpanded(siteId);
    if (details[siteId] && details[siteId] !== 'error') return;
    setDetails((prev) => ({ ...prev, [siteId]: 'loading' }));
    try {
      const data = (await getAdminFramework(siteId)) as RawFramework;
      setDetails((prev) => ({ ...prev, [siteId]: data }));
    } catch {
      setDetails((prev) => ({ ...prev, [siteId]: 'error' }));
      toast({
        title: `Failed to load framework for ${siteId}`,
        status: 'error',
        duration: 3000,
      });
    }
  }

  return (
    <Box bg="white" p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200">
      <HStack justify="space-between" mb={3}>
        <HStack spacing={2}>
          <FileSliders size={18} />
          <Text fontWeight="600">Script Framework Configuration</Text>
        </HStack>
        <Button
          size="xs"
          leftIcon={<RefreshCw size={12} />}
          variant="ghost"
          onClick={refresh}
          isLoading={loading}
        >
          Refresh
        </Button>
      </HStack>

      <Text fontSize="xs" color="gray.500" mb={3}>
        Read-only viewer for the Script Framework Engine config that runs when{' '}
        <Code fontSize="xs">script_framework_engine</Code> is enabled for a site. Use this
        tab to verify which framework version is active and to compare engine output
        against the locked reference script.
      </Text>

      {!summaries && loading && (
        <Box p={4} textAlign="center">
          <Spinner size="sm" color="brand.500" />
          <Text mt={2} fontSize="sm" color="gray.500">
            Loading frameworks…
          </Text>
        </Box>
      )}

      {summaries && summaries.length === 0 && (
        <Text p={3} color="gray.500" fontSize="sm">
          No active sites configured for this organization.
        </Text>
      )}

      <VStack align="stretch" spacing={2}>
        {summaries?.map((s) => {
          const detail = details[s.site_id];
          const isExpanded = expanded === s.site_id;
          return (
            <Box
              key={s.site_id}
              borderWidth={1}
              borderColor="gray.200"
              borderRadius="md"
              p={3}
              bg="white"
            >
              <HStack justify="space-between" wrap="wrap">
                <VStack align="flex-start" spacing={1} flex="1" minW="200px">
                  <HStack spacing={2}>
                    <Text fontWeight="600" fontSize="sm">
                      {s.site_name}
                    </Text>
                    <Code fontSize="xs">{s.site_id}</Code>
                  </HStack>
                  <HStack spacing={2} flexWrap="wrap">
                    <Badge
                      colorScheme={s.engine_enabled ? 'green' : 'gray'}
                      fontSize="2xs"
                    >
                      Engine {s.engine_enabled ? 'ENABLED' : 'OFF'}
                    </Badge>
                    {s.framework_id && (
                      <Tag size="sm" colorScheme="brand">
                        {s.framework_id}
                        {s.framework_version ? ` v${s.framework_version}` : ''}
                      </Tag>
                    )}
                    <Badge colorScheme="purple" fontSize="2xs" textTransform="uppercase">
                      {s.framework_status}
                    </Badge>
                    {s.brand_name && (
                      <Text fontSize="xs" color="gray.500">
                        Brand: {s.brand_name}
                      </Text>
                    )}
                  </HStack>
                </VStack>
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={isExpanded ? <EyeOff size={14} /> : <Eye size={14} />}
                  onClick={() => handleToggle(s.site_id)}
                  isDisabled={!s.framework_id}
                >
                  {isExpanded ? 'Hide' : 'View'} config
                </Button>
              </HStack>
              {isExpanded && (
                <Box mt={3}>
                  {detail === 'loading' && (
                    <HStack p={3} spacing={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color="gray.500">
                        Loading framework…
                      </Text>
                    </HStack>
                  )}
                  {detail === 'error' && (
                    <Text p={3} color="red.500" fontSize="sm">
                      Failed to load framework — try refreshing.
                    </Text>
                  )}
                  {detail && detail !== 'loading' && detail !== 'error' && (
                    <FrameworkDetail data={detail} />
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </VStack>
    </Box>
  );
}
